import { useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from '@ukpostbox/frontend-framework/lib/ReactHookForm';
import {
	Button,
	ButtonSize,
	ColourStateSolid,
	ComboBoxItemDisplayEnum,
	ComboBoxTypeEnum,
	CountryCodeControl,
	FormWrapperMinimum,
	GenericLoader,
	ToggleSwitch,
	ToggleType,
} from '@ukpostbox/frontend-framework';
import { faPoundSign } from '@fortawesome/pro-solid-svg-icons/faPoundSign';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons/faMagnifyingGlass';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faXmarkCircle } from '@fortawesome/pro-solid-svg-icons/faXmarkCircle';
import { ManualEventOnly } from '../../../_Global/_e2e/Events';
import { useForwardingBatchProvider } from '../../Providers/ForwardingBatchProvider';
import { BatchMailItemEntryDescriptionType } from '../../Types/ForwardingBatchTypes';

function BatchMailItemEntryDescription(props: BatchMailItemEntryDescriptionType) {
	const {
		documentsOnly,
		showHarmony,
		mailItemUpdating,
		OpenHarmonyLookup,
		AddMailItemDescription,
		UpdateMailItemDescription,
	} = useForwardingBatchProvider();

	const formMethods = useForm({
		defaultValues: {
			id: props.mailDescription?.id,
			description: props.mailDescription?.description || '',
			descriptionType: props.mailDescription?.descriptionType ? props.mailDescription?.descriptionType : 1,
			originCountryCode: props.mailDescription?.originCountryCode || null,
			commodityCode: props.mailDescription?.commodityCode || null,
			value: props.mailDescription?.value || 0,
			quantity: props.mailDescription?.quantity || 0,
		},
		mode: 'all',
	});

	const [entryDocumentsOnly, setEntryDocumentsOnly] = useState<boolean>(
		documentsOnly || props.mailDescription?.descriptionType === 3,
	);

	const commodityInputRef = useRef<HTMLInputElement | null>(null);
	const formRef = useRef<HTMLFormElement | null>(null);

	const HandleSave = (data: any) => {
		if (!data.id) {
			AddMailItemDescription(props.mailUuid, data).then((r) => {
				if (r.descriptionId) {
					formMethods.setValue('id', r.descriptionId);
				} else {
					Object.entries(r).forEach((entry: any) => {
						entry.forEach((error: string) => {
							formMethods.setError(entry[0], { type: 'custom', message: `${error}` });
						});
					});
				}
			});
		} else {
			UpdateMailItemDescription(props.mailUuid, data.id, data);
		}
	};

	const HandleBlur = () => {
		formMethods.handleSubmit((data) => HandleSave(data));
	};

	const HandleDocumentsOnlyChange = (value: boolean, input?: any) => {
		formMethods.setValue('descriptionType', value ? 3 : 1);

		if (input) {
			ManualEventOnly('blur', formRef.current);
		}

		setEntryDocumentsOnly(value);
		HandleBlur();

		props.itemDocumentsOnlyCallback();
	};

	const HandleBatchDocumentsOnlyChange = (value: boolean) => {
		formMethods.setValue('descriptionType', value ? 3 : 1);

		setEntryDocumentsOnly(value);
		HandleBlur();
	};

	useEffect(() => {
		console.log('documentsOnly', documentsOnly);
		HandleBatchDocumentsOnlyChange(documentsOnly);
	}, [documentsOnly]);

	return (
		<FormProvider {...formMethods}>
			<form
				id={`batchMailItemDescription_${props.mailItemIndex}`}
				ref={formRef}
				onBlur={formMethods.handleSubmit(HandleSave)}
			>
				<fieldset disabled={mailItemUpdating || props.readOnly}>
					<div className='forwarding-batch-item-entry-meta-entry'>
						<Controller
							control={formMethods.control}
							name='descriptionType'
							render={() => (
								<ToggleSwitch
									type={ToggleType.Checkbox}
									name='descriptionType'
									label='Documents Only?'
									defaultChecked={documentsOnly || entryDocumentsOnly}
									onChange={(e, input) => {
										HandleDocumentsOnlyChange(!entryDocumentsOnly, input);
									}}
								/>
							)}
						/>

						{!entryDocumentsOnly && (
							<Controller
								control={formMethods.control}
								name='description'
								shouldUnregister
								rules={!entryDocumentsOnly ? undefined : { required: 'Description is Required' }}
								render={({ field, fieldState }) => (
									<FormWrapperMinimum
										hideValidationMessage
										invalid={fieldState.invalid}
										validationMessage={fieldState.error?.message}
									>
										<input
											type='text'
											placeholder='Description'
											maxLength={90}
											defaultValue={field.value}
											onChange={(e) => field.onChange(e.currentTarget.value)}
											onBlur={() => HandleBlur()}
										/>
									</FormWrapperMinimum>
								)}
							/>
						)}

						<div>
							<Controller
								control={formMethods.control}
								name='quantity'
								rules={{ required: 'Quantity is Required', min: 1 }}
								render={({ field, fieldState }) => (
									<FormWrapperMinimum
										invalid={fieldState.invalid}
										validationMessage={fieldState.error?.message}
									>
										<input
											type='number'
											placeholder='Quantity'
											min={0}
											defaultValue={field.value}
											onChange={(e) => field.onChange(parseInt(e.currentTarget.value, 10))}
											onBlur={() => HandleBlur()}
										/>
									</FormWrapperMinimum>
								)}
							/>
						</div>

						{!entryDocumentsOnly && (
							<Controller
								control={formMethods.control}
								name='value'
								shouldUnregister
								rules={{ required: 'Value is Required', min: 0.01 }}
								render={({ field, fieldState }) => (
									<FormWrapperMinimum
										invalid={fieldState.invalid}
										validationMessage={fieldState.error?.message}
										addonStart={
											<Button
												title='£'
												size={ButtonSize.Small}
												icon={faPoundSign}
												iconOnly
												disabled
											/>
										}
									>
										<input
											type='number'
											placeholder='Value'
											min={0.1}
											step={0.1}
											defaultValue={field.value}
											onChange={(e) => field.onChange(parseInt(e.currentTarget.value, 10))}
											onBlur={() => HandleBlur()}
										/>
									</FormWrapperMinimum>
								)}
							/>
						)}

						{!entryDocumentsOnly && showHarmony && (
							<Controller
								control={formMethods.control}
								name='commodityCode'
								shouldUnregister
								rules={{ required: 'Commodity Code is Required' }}
								render={({ field, fieldState }) => (
									<FormWrapperMinimum
										invalid={fieldState.invalid}
										validationMessage={fieldState.error?.message}
										addonEnd={
											<Button
												title='Search for Commodity Codes'
												size={ButtonSize.Small}
												icon={faMagnifyingGlass}
												iconOnly
												onClick={() => OpenHarmonyLookup(commodityInputRef.current)}
											/>
										}
									>
										<input
											ref={commodityInputRef}
											type='number'
											placeholder='Commodity Code'
											pattern='\d{8,10}'
											min={8}
											max={10}
											defaultValue={field.value ? [field.value] : undefined}
											onChange={(e) => {
												console.log('onChange', e.currentTarget.value);
												field.onChange(e.currentTarget.value);
											}}
											onBlur={() => HandleBlur()}
										/>
									</FormWrapperMinimum>
								)}
							/>
						)}

						{!entryDocumentsOnly && showHarmony && (
							<Controller
								control={formMethods.control}
								name='originCountryCode'
								rules={{ required: 'Country Code is Required' }}
								render={({ field, fieldState, formState }) => (
									<FormWrapperMinimum
										hideValidationMessage
										invalid={fieldState.invalid}
										validationMessage={fieldState.error?.message}
									>
										<CountryCodeControl
											placeholder='Country of Origin'
											name='originCountryCode'
											forwardValuesCallback={(val) => {
												field.onChange(val[0]);
												HandleBlur();
											}}
											defaultValue={field.value ? [field.value] : undefined}
											displayType={ComboBoxTypeEnum.Single}
											displayItemLabelType={ComboBoxItemDisplayEnum.Label}
										/>
									</FormWrapperMinimum>
								)}
							/>
						)}

						{props.removable && (
							<Button
								title='Remove Contents'
								squared
								colour={ColourStateSolid.Secondary}
								size={ButtonSize.Small}
								icon={faTrash}
								iconOnly
								onClick={() => props.removeDescriptionCallback()}
							/>
						)}

						{mailItemUpdating ? (
							<GenericLoader size='2x' />
						) : formMethods.formState.isValid ? (
							<FontAwesomeIcon icon={faCheckCircle} color='var(--success-bg)' size='2x' />
						) : (
							<FontAwesomeIcon icon={faXmarkCircle} color='var(--alert-bg)' size='2x' />
						)}
					</div>
				</fieldset>
			</form>
		</FormProvider>
	);
}

export { BatchMailItemEntryDescription };
